import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  ArrowUpCircle,
  BadgeCheck,
  BadgeDollarSign,
  CheckCircle,
  CircleDot,
  Clock,
  Eye,
  Inbox,
  Package,
  Truck,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { Shipment } from "@/lib/supabase";
import { useShipment } from "@/contexts/shipment-context";
import { useEffect } from "react";

const statusBadgeVariants: Record<string, string> = {
  picked_up_by_recipient:
    "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
  delivered_to_recipient:
    "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
  in_transit_to_country:
    "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
  in_transit_to_recipient:
    "bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200",
  ready_for_pickup:
    "bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200",
  arrived_at_warehouse:
    "bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200",
  processing_at_warehouse:
    "bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200",
  picking_up_from_sender:
    "bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200",
  received_at_warehouse:
    "bg-sky-100 text-sky-800 border-sky-200 hover:bg-sky-200",
  created: "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200",
  estimated: "bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200",
  confirmed: "bg-green-100 text-green-800 border-green-200 hover:bg-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
  refunded: "bg-red-100 text-red-800 border-red-200 hover:bg-red-200",
};

function getStatusIcon(status: string) {
  switch (status) {
    case "picked_up_by_recipient":
    case "delivered_to_recipient":
      return <CheckCircle className="h-4 w-4 text-green-500" />;

    case "in_transit_to_country":
    case "in_transit_to_recipient":
      return <Truck className="h-4 w-4 text-blue-500" />;

    case "ready_for_pickup":
      return <Package className="h-4 w-4 text-purple-500" />;

    case "arrived_at_warehouse":
      return <AlertCircle className="h-4 w-4 text-orange-500" />;

    case "processing_at_warehouse":
      return <Clock className="h-4 w-4 text-yellow-500" />;

    case "picking_up_from_sender":
      return <ArrowUpCircle className="h-4 w-4 text-teal-500" />;

    case "received_at_warehouse":
      return <Inbox className="h-4 w-4 text-sky-500" />;

    case "created":
      return <CircleDot className="h-4 w-4 text-gray-500" />;

    case "estimated":
      return <BadgeDollarSign className="h-4 w-4 text-amber-500" />;

    case "confirmed":
      return <BadgeCheck className="h-4 w-4 text-green-600" />;

    case "cancelled":
    case "refunded":
      return <XCircle className="h-4 w-4 text-red-500" />;

    default:
      return <XCircle className="h-4 w-4 text-gray-500" />;
  }
}

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge
      className={
        statusBadgeVariants[status] ||
        "bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200"
      }
    >
      {status.replace(/_/g, " ").toUpperCase()}
    </Badge>
  );
}

function getTotalItems(shipment: Shipment) {
  return (
    shipment.packages?.reduce(
      (total, pkg) => total + (pkg.items?.length || 0),
      0
    ) || 0
  );
}

export function RecentShipments() {
  const navigate = useNavigate();
  const { fetchRecent, recentShipments: shipments } = useShipment();

  useEffect(() => {
    fetchRecent(10);
  }, [fetchRecent]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between space-x-2">
          <div>
            <CardTitle className="text-xl md:text-2xl">
              Recent Shipments
            </CardTitle>
            <CardDescription>10 latest shipments in the system</CardDescription>
          </div>

          <Link to="/admin/shipments">
            <Button variant="outline">
              All Shipments
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4">
          {shipments &&
            shipments.map((shipment) => (
              <div
                key={shipment.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white shadow-sm transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(shipment.status!)}
                    <div>
                      <p className="font-medium text-gray-900">
                        {shipment.tracking_number}
                      </p>
                      <p className="text-sm text-gray-600">
                        {shipment.customer?.first_name}{" "}
                        {shipment.customer?.last_name}
                      </p>

                      <p className="text-sm text-gray-600">
                        {shipment.route === "us_to_ht"
                          ? "USA to Haiti"
                          : "Haiti to USA"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col mt-4 md:mt-0 md:flex-row md:items-center md:space-x-6">
                  <div className="flex justify-between space-x-6">
                    <div className="text-start md:text-center">
                      <p className="text-sm font-medium text-gray-900">
                        Method
                      </p>
                      <p className="text-xs text-gray-600">
                        {shipment.shipping_method?.toUpperCase()}
                      </p>
                    </div>

                    {shipment.shipping_method === "air" && (
                      <div className="text-right md:text-center">
                        <p className="text-sm font-medium text-gray-900">
                          Total Weight
                        </p>
                        <p className="text-xs text-gray-600">
                          {shipment.total_weight} lbs
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex mt-2 md:mt-0 justify-between space-x-6">
                    <div className="text-start md:text-center">
                      <p className="text-sm font-medium text-gray-900">Items</p>
                      <p className="text-xs text-gray-600">
                        {getTotalItems(shipment)}
                      </p>
                    </div>

                    <div className="text-right md:text-center">
                      <p className="text-sm font-medium text-gray-900">Cost</p>
                      <p className="text-xs text-gray-600">
                        ${shipment.total_cost}
                      </p>
                    </div>
                  </div>

                  <div className="text-start md:text-center mt-2 md:mt-0">
                    <p className="text-sm font-medium text-gray-900">Created</p>
                    <p className="text-xs text-gray-600">
                      {new Date(shipment.created_at!).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="flex justify-center my-4 md:my-0">
                    <div className="flex items-center space-x-2">
                      <StatusBadge status={shipment.status!} />
                    </div>
                  </div>

                  <Link to={`/admin/shipments/${shipment.tracking_number}`}>
                    <Button size="sm">
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                  </Link>
                </div>
              </div>
            ))}

          {(!shipments || shipments.length === 0) && (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No shipments found</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
